import React, { useState, useEffect } from 'react';
import './DiarioModule.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface Motorista {
  id: string;
  nome: string;
  dataAdmissao: string;
  funcao: string;
  responsavel: string;
  diasATrabalhar: number;
  diasTrabalhados: number; // Dias úteis trabalhados no período
  naoTrabalhados: number;
  diasCalendario: boolean[];
  percentual: number;
  temAlerta?: boolean; // Se tem alertas de manifestos
  alertas?: any[]; // Lista de alertas
}

interface MotoristaCadastro {
  cpf: string;
  nome: string;
  dataCriacao?: string | null;
}

const DiarioModule: React.FC = () => {
  const [motoristas, setMotoristas] = useState<Motorista[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [tableRowsPerPage, setTableRowsPerPage] = useState(5);
  const [tablePage, setTablePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('todos-indicadores');
  const [gestorFilter, setGestorFilter] = useState('todos');
  const [periodoFilter, setPeriodoFilter] = useState(() => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(false);
  const [showMotoristasDisponiveisModal, setShowMotoristasDisponiveisModal] = useState(false);
  const [showMotoristasLicencaModal, setShowMotoristasLicencaModal] = useState(false);
  const [showManifestosAlertaModal, setShowManifestosAlertaModal] = useState(false);
  const [showCadastroMotoristasModal, setShowCadastroMotoristasModal] = useState(false);
  const [motoristasDisponiveis, setMotoristasDisponiveis] = useState<any[]>([]);
  const [motoristasDisponiveisCompletos, setMotoristasDisponiveisCompletos] = useState<any[]>([]);
  const [motoristasLicenca, setMotoristasLicenca] = useState<any[]>([]);
  const [ausencias, setAusencias] = useState<any[]>([]);
  const [manifestos, setManifestos] = useState<any[]>([]);
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  const [expandedMotoristaId, setExpandedMotoristaId] = useState<string | null>(null);
  const [motoristasCadastroOpcoes, setMotoristasCadastroOpcoes] = useState<MotoristaCadastro[]>([]);
  const [totalMotoristasCatalogo, setTotalMotoristasCatalogo] = useState(0);
  const [motoristasCadastroSelecionados, setMotoristasCadastroSelecionados] = useState<Set<string>>(new Set());
  const [filtroCadastroMotoristas, setFiltroCadastroMotoristas] = useState('');
  const [loadingCadastroMotoristas, setLoadingCadastroMotoristas] = useState(false);
  const [salvandoCadastroMotoristas, setSalvandoCadastroMotoristas] = useState(false);

  const resetFiltros = () => {
    const hoje = new Date();
    setSearchTerm('');
    setGestorFilter('todos');
    setSortOption('todos-indicadores');
    setPeriodoFilter(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`);
  };

  // Função para alternar flip do card
  const toggleCardFlip = (motoristaId: string) => {
    const newFlippedCards = new Set(flippedCards);
    if (newFlippedCards.has(motoristaId)) {
      newFlippedCards.delete(motoristaId);
    } else {
      newFlippedCards.add(motoristaId);
    }
    setFlippedCards(newFlippedCards);
  };

  const toggleExpandedMotorista = (motoristaId: string) => {
    setExpandedMotoristaId((currentId) => (currentId === motoristaId ? null : motoristaId));
  };

  const abrirModalCadastroMotoristas = async () => {
    setShowCadastroMotoristasModal(true);
    setLoadingCadastroMotoristas(true);
    try {
      const [opcoesResp, selecionadosResp] = await Promise.all([
        fetch('/api/diario/motoristas-cadastro-opcoes'),
        fetch('/api/diario/motoristas-selecionados')
      ]);

      const opcoesData = await opcoesResp.json();
      const selecionadosData = await selecionadosResp.json();

      const opcoes = Array.isArray(opcoesData) ? opcoesData : [];
      const selecionados = Array.isArray(selecionadosData) ? selecionadosData : [];

      setMotoristasCadastroOpcoes(opcoes);
      setMotoristasCadastroSelecionados(new Set(selecionados.map((m: any) => String(m.CPF || '').trim()).filter(Boolean)));
    } catch (error) {
      console.error('Erro ao carregar motoristas para cadastro:', error);
      setMotoristasCadastroOpcoes([]);
      setMotoristasCadastroSelecionados(new Set());
    } finally {
      setLoadingCadastroMotoristas(false);
    }
  };

  const alternarSelecaoMotoristaCadastro = (cpf: string) => {
    setMotoristasCadastroSelecionados((anterior) => {
      const proximo = new Set(anterior);
      if (proximo.has(cpf)) {
        proximo.delete(cpf);
      } else {
        proximo.add(cpf);
      }
      return proximo;
    });
  };

  const salvarMotoristasCadastro = async () => {
    setSalvandoCadastroMotoristas(true);
    try {
      const payload = {
        motoristas: motoristasCadastroOpcoes
          .filter((m) => motoristasCadastroSelecionados.has(String(m.cpf).trim()))
          .map((m) => ({ cpf: String(m.cpf).trim(), nome: String(m.nome).trim() }))
      };

      const response = await fetch('/api/diario/motoristas-selecionados', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const erro = await response.text();
        throw new Error(erro || 'Falha ao salvar seleção de motoristas');
      }

      setShowCadastroMotoristasModal(false);
      await buscarDados();
    } catch (error) {
      console.error('Erro ao salvar seleção de motoristas:', error);
      alert('Não foi possível salvar os motoristas selecionados. Verifique o backend e tente novamente.');
    } finally {
      setSalvandoCadastroMotoristas(false);
    }
  };

  // Função para obter todos os dias do mês
  const getDiasDoMes = (anoMes: string): number[] => {
    const [ano, mes] = anoMes.split('-').map(Number);
    const diasNoMes = new Date(ano, mes, 0).getDate();
    return Array.from({ length: diasNoMes }, (_, i) => i + 1);
  };

  // Função para gerar dados de trabalho baseados no mês
  const generateMonthWorkData = (anoMes: string): boolean[] => {
    const diasDoMes = getDiasDoMes(anoMes);
    return diasDoMes.map(() => Math.random() > 0.35);
  };

  // Função para calcular dias úteis no mês
  const getDiasUteisNoMes = (anoMes: string): number => {
    const [ano, mes] = anoMes.split('-').map(Number);
    const diasNoMes = new Date(ano, mes, 0).getDate();
    let diasUteis = 0;
    
    for (let dia = 1; dia <= diasNoMes; dia++) {
      const data = new Date(ano, mes - 1, dia);
      const diaSemana = data.getDay();
      if (diaSemana >= 1 && diaSemana <= 5) {
        diasUteis++;
      }
    }
    return diasUteis;
  };

  const getCalendarSlots = (anoMes: string, diasCalendario: boolean[]) => {
    const [ano, mes] = anoMes.split('-').map(Number);
    const primeiroDia = new Date(ano, mes - 1, 1);
    const diasNoMes = new Date(ano, mes, 0).getDate();
    const diasNoMesAnterior = new Date(ano, mes - 1, 0).getDate();
    const diaSemanaInicio = (primeiroDia.getDay() + 6) % 7;

    const slots: Array<{ dia: number; worked: boolean; outside: boolean }> = [];

    for (let i = diaSemanaInicio - 1; i >= 0; i--) {
      slots.push({
        dia: diasNoMesAnterior - i,
        worked: false,
        outside: true
      });
    }

    for (let dia = 1; dia <= diasNoMes; dia++) {
      slots.push({
        dia,
        worked: Boolean(diasCalendario[dia - 1]),
        outside: false
      });
    }

    while (slots.length % 7 !== 0) {
      slots.push({
        dia: slots.length - (diaSemanaInicio + diasNoMes) + 1,
        worked: false,
        outside: true
      });
    }

    return slots;
  };

  const getWeekRanges = (anoMes: string) => {
    const [ano, mes] = anoMes.split('-').map(Number);
    const primeiroDia = new Date(ano, mes - 1, 1);
    const ultimoDia = new Date(ano, mes, 0);

    const inicio = new Date(primeiroDia);
    const deslocamentoInicio = (inicio.getDay() + 6) % 7;
    inicio.setDate(inicio.getDate() - deslocamentoInicio);

    const fim = new Date(ultimoDia);
    const deslocamentoFim = 7 - (((fim.getDay() + 6) % 7) + 1);
    fim.setDate(fim.getDate() + deslocamentoFim);

    const semanas: Array<{ label: string; start: Date; end: Date }> = [];
    let cursor = new Date(inicio);
    let indice = 1;

    while (cursor <= fim) {
      const semanaInicio = new Date(cursor);
      const semanaFim = new Date(cursor);
      semanaFim.setDate(semanaFim.getDate() + 6);

      semanas.push({
        label: `Sem ${indice}`,
        start: semanaInicio,
        end: semanaFim
      });

      cursor.setDate(cursor.getDate() + 7);
      indice += 1;
    }

    return semanas;
  };

  const formatarFaixaSemana = (start: Date, end: Date) => {
    const formatar = (data: Date) =>
      `${String(data.getDate()).padStart(2, '0')}/${String(data.getMonth() + 1).padStart(2, '0')}`;
    return `${formatar(start)} - ${formatar(end)}`;
  };

  const formatarDataHora = (valor: string | null | undefined) => {
    if (!valor) return '-';
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return '-';

    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatarTrecho = (cidade?: string | null, uf?: string | null) => {
    const partes = [cidade, uf].filter(Boolean);
    return partes.length > 0 ? partes.join(' / ') : '-';
  };

  const getInitials = (nome: string) => {
    return nome
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((parte) => parte[0])
      .join('')
      .toUpperCase();
  };

  const getAvatarColor = (nome: string) => {
    const cores = ['#3b82f6', '#7c3aed', '#f97316', '#16a34a', '#ef4444', '#0ea5e9'];
    const hash = nome.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return cores[hash % cores.length];
  };

  const getManifestoDate = (manifesto: any) => {
    const dataBase = manifesto.PARTIDA || manifesto.CHEGADA || manifesto.CRIACAO || manifesto.REGISTRO;
    if (!dataBase) return null;
    const data = new Date(dataBase);
    return Number.isNaN(data.getTime()) ? null : data;
  };

  const getManifestosPorSemana = (manifestosMotorista: any[], semanas: Array<{ label: string; start: Date; end: Date }>) => {
    return semanas.map((semana) => {
      const total = manifestosMotorista.filter((manifesto) => {
        const data = getManifestoDate(manifesto);
        if (!data) return false;
        return data >= semana.start && data <= semana.end;
      }).length;

      return {
        ...semana,
        total
      };
    });
  };

  const getManifestoOperacao = (manifesto: any) => {
    const operacao = String(manifesto?.TOMADOR || '').trim();
    return operacao || 'Não informado';
  };

  const normalizarOperacao = (valor: string) => String(valor || '').trim().toLowerCase();

  const getAlertasManifestos = (manifestosMotorista: any[]) => {
    const alertas: any[] = [];
    const manifestosOrdenados = [...manifestosMotorista].sort((a, b) => {
      const dataA = getManifestoDate(a);
      const dataB = getManifestoDate(b);
      return (dataA?.getTime() || 0) - (dataB?.getTime() || 0);
    });

    manifestosOrdenados.forEach((manifesto, index) => {
      if (manifesto.STATUS === 'Trânsito' && !manifesto.CHEGADA) {
        const manifestosPosteriores = manifestosOrdenados.slice(index + 1);
        if (manifestosPosteriores.length > 0) {
          const dataReferencia = manifesto.PARTIDA || manifesto.CRIACAO || manifesto.REGISTRO;
          const dataFormatada = dataReferencia ? new Date(dataReferencia).toLocaleDateString('pt-BR') : 'data não informada';

          alertas.push({
            tipo: 'MANIFESTO_EM_TRANSITO_COM_POSTERIORES',
            manifesto: manifesto.NUMERO,
            dataPartida: manifesto.PARTIDA,
            dataCriacao: manifesto.CRIACAO || manifesto.REGISTRO,
            descricao: `Manifesto ${manifesto.NUMERO} em trânsito desde ${dataFormatada} com manifestos criados posteriormente`
          });
        }
      }

      if (manifesto.STATUS === 'Finalizado' && !manifesto.CHEGADA) {
        alertas.push({
          tipo: 'MANIFESTO_FINALIZADO_SEM_CHEGADA',
          manifesto: manifesto.NUMERO,
          dataPartida: manifesto.PARTIDA,
          dataCriacao: manifesto.CRIACAO || manifesto.REGISTRO,
          descricao: `Manifesto ${manifesto.NUMERO} finalizado sem data de chegada`
        });
      }
    });

    return alertas;
  };

  const calcularMotoristaPorManifestos = (motorista: Motorista, manifestosMotorista: any[], periodo: string, operacaoSelecionada: string): Motorista => {
    const diasDoMes = getDiasDoMes(periodo);
    const diasUteisNoMes = getDiasUteisNoMes(periodo);
    const [ano, mes] = periodo.split('-').map(Number);
    const dataInicioPeriodo = new Date(ano, mes - 1, 1);
    const dataFimPeriodo = new Date(ano, mes - 1, diasDoMes.length, 23, 59, 59, 999);
    const hoje = new Date();
    const diasTrabalhadosSet = new Set<string>();

    manifestosMotorista.forEach((manifesto) => {
      const dataPartida = manifesto.PARTIDA ? new Date(manifesto.PARTIDA) : null;
      const dataChegada = manifesto.CHEGADA ? new Date(manifesto.CHEGADA) : null;

      if (!dataPartida || Number.isNaN(dataPartida.getTime())) {
        return;
      }

      const adicionarDia = (data: Date) => {
        if (data >= dataInicioPeriodo && data <= dataFimPeriodo) {
          diasTrabalhadosSet.add(data.toISOString().split('T')[0]);
        }
      };

      if (manifesto.STATUS === 'Trânsito' && !dataChegada) {
        const cursor = new Date(dataPartida);
        const limite = hoje > dataFimPeriodo ? dataFimPeriodo : hoje;

        while (cursor <= limite) {
          adicionarDia(cursor);
          cursor.setDate(cursor.getDate() + 1);
        }
        return;
      }

      if (manifesto.STATUS === 'Finalizado' && dataChegada && !Number.isNaN(dataChegada.getTime())) {
        const cursor = new Date(dataPartida);
        while (cursor <= dataChegada) {
          adicionarDia(cursor);
          cursor.setDate(cursor.getDate() + 1);
        }
        return;
      }

      adicionarDia(dataPartida);
    });

    const diasCalendario = diasDoMes.map((dia) => diasTrabalhadosSet.has(`${periodo}-${String(dia).padStart(2, '0')}`));

    let diasTrabalhadosUteis = 0;
    diasDoMes.forEach((dia, idx) => {
      const data = new Date(ano, mes - 1, dia);
      const diaSemana = data.getDay();
      if (diaSemana >= 1 && diaSemana <= 5 && diasCalendario[idx]) {
        diasTrabalhadosUteis += 1;
      }
    });

    const naoTrabalhadosUteis = Math.max(0, diasUteisNoMes - diasTrabalhadosUteis);
    const percentual = diasUteisNoMes > 0 ? Math.round((diasTrabalhadosUteis / diasUteisNoMes) * 100) : 0;
    const alertas = getAlertasManifestos(manifestosMotorista);

    const operacoesOrdenadas = Object.entries(
      manifestosMotorista.reduce((acc, manifesto) => {
        const operacao = getManifestoOperacao(manifesto);
        acc[operacao] = (acc[operacao] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    ) as Array<[string, number]>;

    operacoesOrdenadas.sort((a, b) => b[1] - a[1]);

    return {
      ...motorista,
      responsavel: operacaoSelecionada === 'todos'
        ? (operacoesOrdenadas[0]?.[0] || 'Não informado')
        : operacaoSelecionada,
      diasATrabalhar: diasUteisNoMes,
      diasTrabalhados: diasTrabalhadosUteis,
      naoTrabalhados: naoTrabalhadosUteis,
      percentual,
      diasCalendario,
      temAlerta: alertas.length > 0,
      alertas
    };
  };

  // Função para recalcular dados do motorista baseado no período
  // const recalcularDadosPorPeriodo = (motorista: Motorista, periodo: string): Motorista => {
  //   const diasDoMes = getDiasDoMes(periodo);
  //   const totalDiasNoMes = diasDoMes.length;
  //   const diasUteisNoMes = getDiasUteisNoMes(periodo);
  //   // Gera dados de trabalho para todos os dias do mês
  //   const diasCalendario = generateMonthWorkData(periodo);
  //   // Considera apenas os dias úteis para cálculo de trabalhados e não trabalhados
  //   let diasTrabalhados = 0;
  //   let naoTrabalhados = 0;
  //   diasDoMes.forEach((dia, idx) => {
  //     const data = new Date(Number(periodo.split('-')[0]), Number(periodo.split('-')[1]) - 1, dia);
  //     const diaSemana = data.getDay();
  //     if (diaSemana >= 1 && diaSemana <= 5) {
  //       if (diasCalendario[idx]) diasTrabalhados++;
  //       else naoTrabalhados++;
  //     }
  //   });
  //   const percentual = diasUteisNoMes > 0 ? Math.round((diasTrabalhados / diasUteisNoMes) * 100) : 0;
  //   return {
  //     ...motorista,
  //     diasATrabalhar: diasUteisNoMes,
  //     diasTrabalhados,
  //     naoTrabalhados,
  //     percentual,
  //     diasCalendario
  //   };
  // };


  // Busca motoristas reais do backend
  const fetchMotoristas = async (): Promise<Motorista[]> => {
    try {
      const response = await fetch('/api/diario/motoristas-selecionados');
      const text = await response.text();
      if (!response.ok) {
        console.error('Erro ao buscar motoristas: status', response.status, response.statusText, 'body:', text);
        return [];
      }
      try {
        const data = JSON.parse(text);
        if (!Array.isArray(data)) {
          console.error('Resposta inválida para motoristas selecionados (esperado array):', data);
          return [];
        }
        // Adapta os dados recebidos para o formato esperado
        return data.map((item: any) => ({
          id: String(item.CPF || Math.random()),
          nome: item.NOME || 'Sem Nome',
          dataAdmissao: item.DT_ADMISSAO ? new Date(item.DT_ADMISSAO).toLocaleDateString() : '',
          funcao: item.FUNCAO || '',
          responsavel: item.GESTOR || 'Sem Gestor',
          diasATrabalhar: 0,
          diasTrabalhados: 0,
          naoTrabalhados: 0,
          diasCalendario: [],
          percentual: 0
        }));
      } catch (jsonError) {
        console.error('Erro ao fazer parse do JSON dos motoristas:', jsonError, 'body:', text);
        return [];
      }
    } catch (error) {
      console.error('Erro ao buscar motoristas:', error);
      return [];
    }
  };



  // Busca dias trabalhados do backend (considerando manifestos em trânsito)
  const fetchDiasTrabalhados = async (cpfs: string[] | null, dataInicio: string, dataFim: string) => {
    const params = new URLSearchParams();
    params.append('dataInicio', dataInicio);
    params.append('dataFim', dataFim);
    if (cpfs && cpfs.length > 0) {
      cpfs.forEach(cpf => params.append('cpfs', cpf));
    }
    
    // Removido filtro de gestor - busca todos os dados sempre
    const response = await fetch(`/api/diario/dias-trabalhados?${params.toString()}`);
    if (!response.ok) {
      console.error('Erro ao buscar dias trabalhados:', response.status, response.statusText);
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  // Busca motoristas disponíveis para programação
  const fetchMotoristasDisponiveis = async (dataInicio: string, dataFim: string) => {
    const params = new URLSearchParams();
    params.append('dataInicio', dataInicio);
    params.append('dataFim', dataFim);
    
    // Removido filtro de gestor - busca todos os dados sempre
    console.log('Frontend - fetchMotoristasDisponiveis:', { 
      gestorFilter, 
      params: params.toString(),
      note: 'Filtro de gestor será aplicado no frontend'
    });
    
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 12000);
    const response = await fetch(`/api/diario/motoristas-disponiveis?${params.toString()}`, { signal: controller.signal });
    window.clearTimeout(timeoutId);
    if (!response.ok) {
      console.error('Erro ao buscar motoristas disponíveis:', response.status, response.statusText);
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  // Busca ausências dos motoristas (atestados, férias, etc.)
  const fetchAusenciasMotoristas = async (dataInicio: string, dataFim: string, ids?: string[]) => {
    if (!ids || ids.length === 0) return [];

    const params = new URLSearchParams();
    params.append('dataInicio', dataInicio);
    params.append('dataFim', dataFim);
    
    if (ids && ids.length > 0) {
      ids.forEach(id => params.append('ids', id));
    }
    
    console.log('Frontend - fetchAusenciasMotoristas:', { 
      params: params.toString()
    });
    
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 12000);
    const response = await fetch(`/api/diario/ausencias-motoristas?${params.toString()}`, { signal: controller.signal });
    window.clearTimeout(timeoutId);
    if (!response.ok) {
      console.error('Erro ao buscar ausências de motoristas:', response.status, response.statusText);
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  // Busca manifestos do backend para os CPFs e período informados
  const fetchManifestos = async (cpfs: string[], dataInicio: string, dataFim: string) => {
    if (cpfs.length === 0) return [];
    const params = new URLSearchParams();
    params.append('dataInicio', dataInicio);
    params.append('dataFim', dataFim);
    cpfs.forEach(cpf => params.append('cpfs', cpf));
    const response = await fetch(`/api/diario/manifestos?${params.toString()}`);
    if (!response.ok) {
      console.error('Erro ao buscar manifestos:', response.status, response.statusText);
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  };

  // Calcula os dias trabalhados reais do motorista com base nos dias retornados da API
  const calcularDiasTrabalhados = (motorista: Motorista, diasTrabalhadosData: any[], periodo: string): Motorista => {
    const diasDoMes = getDiasDoMes(periodo);
    const diasUteisNoMes = getDiasUteisNoMes(periodo);
    
    // Encontra os dados do motorista
    const dadosMotorista = diasTrabalhadosData.find(d => String(d.cpf) === motorista.id);
    
    if (!dadosMotorista) {
      // Se não encontrar dados, retorna zerado
      return {
        ...motorista,
        diasATrabalhar: diasUteisNoMes,
        diasTrabalhados: 0,
        naoTrabalhados: diasUteisNoMes,
        percentual: 0,
        diasCalendario: new Array(diasDoMes.length).fill(false)
      };
    }

    // Marca os dias do mês que o motorista trabalhou
    const diasCalendario = diasDoMes.map(dia => {
      const diaFormatado = `${periodo}-${String(dia).padStart(2, '0')}`;
      return dadosMotorista.diasTrabalhados.includes(diaFormatado);
    });

    // Debug para motorista específico
    if (motorista.nome.includes('ANTONIO SERGIO')) {
      console.log('=== DEBUG ANTONIO SERGIO ===');
      console.log('CPF do motorista:', motorista.id);
      console.log('Dados encontrados:', dadosMotorista);
      console.log('Dias trabalhados do backend:', dadosMotorista.diasTrabalhados);
      console.log('Dias do mês:', diasDoMes);
      console.log('Dias úteis no mês:', diasUteisNoMes);
      console.log('Calendário calculado:', diasCalendario);
    }

    // Conta dias trabalhados e não trabalhados
    let diasTrabalhadosUteis = 0; // Apenas dias úteis
    let naoTrabalhadosUteis = 0;  // Dias úteis não trabalhados
    
    diasDoMes.forEach((dia, idx) => {
      const data = new Date(Number(periodo.split('-')[0]), Number(periodo.split('-')[1]) - 1, dia);
      const diaSemana = data.getDay();
      const trabalhouNoDia = diasCalendario[idx];
      
      // Contar apenas dias úteis para o cálculo de percentual
      if (diaSemana >= 1 && diaSemana <= 5) { // Segunda a sexta
        if (trabalhouNoDia) {
          diasTrabalhadosUteis++;
        } else {
          naoTrabalhadosUteis++;
        }
      }
    });

    // Debug adicional para Antonio Sergio
    if (motorista.nome.includes('ANTONIO SERGIO')) {
      console.log('Contagem corrigida:');
      console.log('- Dias trabalhados apenas ÚTEIS:', diasTrabalhadosUteis);
      console.log('- Dias não trabalhados (apenas úteis):', naoTrabalhadosUteis);
      console.log('- Total dias úteis no mês:', diasUteisNoMes);
      console.log('- Percentual baseado em dias úteis:', Math.round((diasTrabalhadosUteis / diasUteisNoMes) * 100));
    }

    // Percentual baseado apenas nos dias úteis trabalhados
    const percentual = diasUteisNoMes > 0 ? Math.round((diasTrabalhadosUteis / diasUteisNoMes) * 100) : 0;

    return {
      ...motorista,
      diasATrabalhar: diasUteisNoMes,
      diasTrabalhados: diasTrabalhadosUteis, // Dias úteis trabalhados
      naoTrabalhados: naoTrabalhadosUteis, // Dias úteis não trabalhados
      percentual,
      diasCalendario,
      temAlerta: dadosMotorista.temAlerta || false,
      alertas: dadosMotorista.alertas || []
    };
  };

  const buscarDados = async () => {
    setLoading(true);
    try {
      const [ano, mes] = periodoFilter.split('-').map(Number);
      const dataInicio = `${ano}-${String(mes).padStart(2, '0')}-01`;
      const dataFim = `${ano}-${String(mes).padStart(2, '0')}-${getDiasDoMes(periodoFilter).length}`;

      // Fonte principal dos cards: motoristas realmente utilizados no mês (com manifesto no período).
      const diasTrabalhadosApi = await fetchDiasTrabalhados(null, dataInicio, dataFim);
      const motoristasApi: Motorista[] = (Array.isArray(diasTrabalhadosApi) ? diasTrabalhadosApi : []).map((item: any) => ({
        id: String(item.cpf || item.CPF || ''),
        nome: item.motorista || item.MOTORISTA || 'Sem Nome',
        dataAdmissao: '',
        funcao: 'MOTORISTA',
        responsavel: item.gestor || item.GESTOR || 'Não informado',
        diasATrabalhar: 0,
        diasTrabalhados: 0,
        naoTrabalhados: 0,
        diasCalendario: [],
        percentual: 0
      })).filter((m: Motorista) => m.id);

      const cpfs = motoristasApi.map(m => m.id);
      const cpfsSelecionados = new Set(cpfs.map(cpf => String(cpf)));

      if (cpfs.length === 0) {
        setMotoristas([]);
        setManifestos([]);
        setAusencias([]);
        setMotoristasDisponiveisCompletos([]);
        setMotoristasLicenca([]);
        setLoading(false);
        return;
      }

      // Carregamento principal (rápido): o card do Diário não depende de disponíveis/ausências para renderizar.
      const manifestosApi = await fetchManifestos(cpfs, dataInicio, dataFim);

      setManifestos(Array.isArray(manifestosApi) ? manifestosApi : []);

      const diasApi = Array.isArray(diasTrabalhadosApi) ? diasTrabalhadosApi : [];
      const dadosReais = motoristasApi.map(motorista =>
        calcularDiasTrabalhados(motorista, diasApi, periodoFilter)
      );
      setMotoristas(dadosReais);
      setLoading(false);

      // Carregamento secundário (pode ser mais lento): não trava a UI principal.
      void (async () => {
        try {
          const motoristasDisponiveisApi = await fetchMotoristasDisponiveis(dataInicio, dataFim);
          const motoristasDisponiveisSelecionados = (motoristasDisponiveisApi || []).filter((m: any) =>
            cpfsSelecionados.has(String(m.cpf || '').trim())
          );

          const idsMotoristas = motoristasDisponiveisSelecionados.map((m: any) => m.id).filter((id: any) => id);
          const ausenciasApi = await fetchAusenciasMotoristas(dataInicio, dataFim, idsMotoristas);

          const temAusenciaNoPeriodo = (motoristaId: number, dataInicioFiltro: string, dataFimFiltro: string) => {
            return (ausenciasApi || []).some((ausencia: any) => {
              if (ausencia.ID_COLABORADOR !== motoristaId) return false;

              const inicioAusencia = new Date(ausencia.INICIO);
              const terminoAusencia = new Date(ausencia.TERMINO);
              const inicioConsulta = new Date(dataInicioFiltro);
              const fimConsulta = new Date(dataFimFiltro);

              return inicioAusencia <= fimConsulta && terminoAusencia >= inicioConsulta;
            });
          };

          const motoristasRealmenteDisponiveis: any[] = [];
          const motoristasEmLicenca: any[] = [];

          motoristasDisponiveisSelecionados.forEach((motorista: any) => {
            if (temAusenciaNoPeriodo(motorista.id, dataInicio, dataFim)) {
              const ausenciasMotorista = (ausenciasApi || []).filter((a: any) => a.ID_COLABORADOR === motorista.id);
              motoristasEmLicenca.push({
                ...motorista,
                ausencias: ausenciasMotorista
              });
            } else {
              motoristasRealmenteDisponiveis.push(motorista);
            }
          });

          setAusencias(Array.isArray(ausenciasApi) ? ausenciasApi : []);
          setMotoristasDisponiveisCompletos(motoristasRealmenteDisponiveis);
          setMotoristasLicenca(motoristasEmLicenca);
        } catch (secondaryError) {
          console.error('Erro no carregamento secundário do Diário:', secondaryError);
          setAusencias([]);
          setMotoristasDisponiveisCompletos([]);
          setMotoristasLicenca([]);
        }
      })();
    } catch (error) {
      console.error('Erro ao carregar dados do Diário:', error);
      setMotoristas([]);
      setManifestos([]);
      setAusencias([]);
      setMotoristasDisponiveisCompletos([]);
      setMotoristasLicenca([]);
      setLoading(false);
    }
  };


  const handlePrint = () => {
    window.print();
  };

  // Exportar para PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text('Relatório de Dias Trabalhados', 14, 16);
    autoTable(doc, {
      head: [['Nome', 'Gestor', 'Função', 'Dias Trabalhados', 'Dias a Trabalhar', 'Faltas', 'Percentual']],
      body: motoristasOrdenados.map(m => [
        m.nome,
        m.responsavel,
        m.funcao,
        m.diasTrabalhados,
        m.diasATrabalhar,
        m.naoTrabalhados,
        `${m.percentual}%`
      ]),
      startY: 22
    });
    doc.save('relatorio-dias-trabalhados.pdf');
  };

  // Exportar para Excel
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(motoristasOrdenados.map(m => ({
      Nome: m.nome,
      Gestor: m.responsavel,
      Função: m.funcao,
      'Dias Trabalhados': m.diasTrabalhados,
      'Dias a Trabalhar': m.diasATrabalhar,
      Faltas: m.naoTrabalhados,
      Percentual: `${m.percentual}%`
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Relatório');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'relatorio-dias-trabalhados.xlsx');
  };

  useEffect(() => {
    buscarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoFilter]); // Removido gestorFilter - só busca quando período muda

  useEffect(() => {
    setTablePage(1);
    setExpandedMotoristaId(null);
  }, [searchTerm, gestorFilter, sortOption, periodoFilter, viewMode]);

  useEffect(() => {
    const carregarTotalCatalogo = async () => {
      try {
        const response = await fetch('/api/diario/motoristas-cadastro-opcoes');
        if (!response.ok) {
          setTotalMotoristasCatalogo(0);
          return;
        }
        const data = await response.json();
        setTotalMotoristasCatalogo(Array.isArray(data) ? data.length : 0);
      } catch (error) {
        console.error('Erro ao carregar total de motoristas do catálogo:', error);
        setTotalMotoristasCatalogo(0);
      }
    };

    carregarTotalCatalogo();
  }, []);

  // Filtrar dados localmente quando gestor muda (sem nova requisição ao banco)
  useEffect(() => {
    if (gestorFilter === 'todos') {
      setMotoristasDisponiveis(motoristasDisponiveisCompletos);
    } else {
      const filtrados = motoristasDisponiveisCompletos.filter(m => 
        m.gestor && m.gestor.toLowerCase() === gestorFilter.toLowerCase()
      );
      setMotoristasDisponiveis(filtrados);
    }
  }, [gestorFilter, motoristasDisponiveisCompletos]);

  // Filtrar motoristas de licença por gestor
  useEffect(() => {
    if (gestorFilter === 'todos') {
      // Manter todos os motoristas de licença quando filtro for "todos"
    } else {
      // Filtrar motoristas de licença por gestor se necessário
      // Como setMotoristasLicenca já está sendo setado em buscarDados,
      // podemos criar uma versão filtrada se necessário
    }
  }, [gestorFilter, motoristasLicenca]);

  const operacoes = ['todos', ...Array.from(new Set(manifestos.map((manifesto: any) => getManifestoOperacao(manifesto))))];

  const manifestosFiltradosPorOperacao = gestorFilter === 'todos'
    ? manifestos
    : manifestos.filter((manifesto: any) => normalizarOperacao(getManifestoOperacao(manifesto)) === normalizarOperacao(gestorFilter));

  const cpfsManifestosFiltrados = new Set(manifestosFiltradosPorOperacao.map((manifesto: any) => String(manifesto.CPF_MOTORISTA)));

  const motoristasComOperacao = motoristas
    .filter((motorista) => cpfsManifestosFiltrados.has(String(motorista.id)))
    .map((motorista) => {
      const manifestosDoMotorista = manifestosFiltradosPorOperacao.filter((manifesto: any) => String(manifesto.CPF_MOTORISTA) === motorista.id);
      return calcularMotoristaPorManifestos(motorista, manifestosDoMotorista, periodoFilter, gestorFilter);
    });

  const motoristasFiltrados = motoristasComOperacao.filter((motorista) =>
    motorista.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const cpfsMotoristasFiltrados = new Set(motoristasFiltrados.map((motorista) => String(motorista.id)));
  const manifestosFiltrados = manifestosFiltradosPorOperacao.filter((manifesto: any) =>
    cpfsMotoristasFiltrados.has(String(manifesto.CPF_MOTORISTA))
  );
  const getManifestosDoMotorista = (motoristaId: string) =>
    manifestosFiltrados.filter((manifesto: any) => String(manifesto.CPF_MOTORISTA) === motoristaId);

  const motoristasSemManifesto = React.useMemo(() => {
    return motoristasFiltrados
      .filter((motorista) => getManifestosDoMotorista(motorista.id).length === 0)
      .map((motorista) => {
        const ultManifesto = manifestosFiltrados
          .filter((manifesto: any) => String(manifesto.CPF_MOTORISTA) === motorista.id)
          .sort((a: any, b: any) => new Date(b.PARTIDA || b.CHEGADA || 0).getTime() - new Date(a.PARTIDA || a.CHEGADA || 0).getTime())[0];

        return {
          nome: motorista.nome,
          responsavel: motorista.responsavel,
          ultimoManifesto: ultManifesto ? (ultManifesto.PARTIDA || ultManifesto.CHEGADA) : null
        };
      });
  }, [motoristasFiltrados, manifestosFiltrados]);

  // Ordenar motoristas
  const motoristasOrdenados = [...motoristasFiltrados].sort((a, b) => {
    switch (sortOption) {
      case 'todos-indicadores':
        return 0;
      case 'menos-trabalhados':
        return a.diasTrabalhados - b.diasTrabalhados;
      case 'mais-trabalhados':
        return b.diasTrabalhados - a.diasTrabalhados;
      case 'nome-az':
        return a.nome.localeCompare(b.nome);
      case 'nome-za':
        return b.nome.localeCompare(a.nome);
      default:
        return 0;
    }
  });

  const weekRanges = getWeekRanges(periodoFilter);
  const tableRowsPerPageOptions = Array.from(
    new Set([5, 10, 15, 20, motoristasOrdenados.length].filter((value) => value > 0 && value <= motoristasOrdenados.length))
  ).sort((a, b) => a - b);
  const totalTablePages = Math.max(1, Math.ceil(motoristasOrdenados.length / tableRowsPerPage));
  const currentTablePage = Math.min(tablePage, totalTablePages);
  const tableStartIndex = (currentTablePage - 1) * tableRowsPerPage;
  const tableEndIndex = tableStartIndex + tableRowsPerPage;
  const motoristasPaginados = motoristasOrdenados.slice(tableStartIndex, tableEndIndex);
  const tableColumnCount = 7 + weekRanges.length;

  return (
    <div className="diario-relatorio">
      <div className="filters-section">
        <div className="filters-row">
          <input
            type="text"
            placeholder="Buscar motorista por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          
          <select 
            className="gestor-select"
            value={gestorFilter}
            onChange={(e) => setGestorFilter(e.target.value)}
          >
            {operacoes.map(gestor => (
              <option key={gestor} value={gestor}>
                {gestor === 'todos' ? 'Operação' : gestor}
              </option>
            ))}
          </select>

          <input
            type="month"
            value={periodoFilter}
            onChange={(e) => setPeriodoFilter(e.target.value)}
            className="periodo-input"
            title="Selecionar mês/ano"
          />
          
          <select 
            className="order-select"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="todos-indicadores">Todos Indicadores</option>
            <option value="menos-trabalhados">Menos trabalhados</option>
            <option value="mais-trabalhados">Mais trabalhados</option>
            <option value="nome-az">Nome A-Z</option>
            <option value="nome-za">Nome Z-A</option>
          </select>

          <button className="clear-filters-btn" onClick={resetFiltros}>
            Limpar filtros
          </button>

        </div>
      </div>

      <div className="stats-section">
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-number">{motoristasFiltrados.length}</div>
              <div className="stat-label">Motoristas exibidos</div>
              <div className="stat-sub">Total na seleção</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🧾</div>
            <div className="stat-content">
              <div className="stat-number">{totalMotoristasCatalogo}</div>
              <div className="stat-label">Total cadastrado</div>
              <div className="stat-sub">Cadastrados</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-number">{getDiasUteisNoMes(periodoFilter)}</div>
              <div className="stat-label">Dias uteis no periodo</div>
              <div className="stat-sub">{new Date(`${periodoFilter}-01`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</div>
            </div>
          </div>
          <div className="stat-card stat-clickable" onClick={() => setShowMotoristasDisponiveisModal(true)}>
            <div className="stat-icon">📝</div>
            <div className="stat-content">
              <div className="stat-number">{(() => {
              // Calcular motoristas únicos para programar (sem duplicatas e sem os de licença)
              const cpfsUnicos = new Set<string>();
              
              // Criar set de CPFs dos motoristas de licença para exclusão
              const cpfsMotorístasLicenca = new Set(motoristasLicenca.map(m => m.cpf));
              
              // Adicionar CPFs dos motoristas sem manifesto (se não estão de licença)
              motoristasSemManifesto.forEach(m => {
                const motoristaCompleto = motoristas.find(motor => motor.nome === m.nome);
                const cpf = motoristaCompleto?.id || m.nome;
                if (!cpfsMotorístasLicenca.has(cpf)) {
                  cpfsUnicos.add(cpf);
                }
              });
              
              // Adicionar CPFs dos motoristas disponíveis (se não duplicados e não estão de licença)
              motoristasDisponiveis.forEach(m => {
                if (!cpfsMotorístasLicenca.has(m.cpf)) {
                  cpfsUnicos.add(m.cpf);
                }
              });
              
              return cpfsUnicos.size;
            })()}</div>
              <div className="stat-label">Para programar</div>
              <div className="stat-sub">Planejamento</div>
            </div>
          </div>
          <div className="stat-card stat-clickable" onClick={() => setShowManifestosAlertaModal(true)}>
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <div className="stat-number">{(() => {
              // Contar manifestos com alertas apenas dos motoristas filtrados
              let totalAlertas = 0;
              motoristasFiltrados.forEach(motorista => {
                if (motorista.temAlerta && motorista.alertas) {
                  totalAlertas += motorista.alertas.length;
                }
              });
              return totalAlertas;
            })()}</div>
              <div className="stat-label">Manifestacoes com problemas</div>
              <div className="stat-sub">Pendencias</div>
            </div>
          </div>
        </div>
      </div>

      <div className="view-toolbar">
        <div className="view-toggle-group">
          <span className="view-toggle-label">Visualização:</span>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
          >
            Cards
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            Tabela
          </button>
        </div>

        {viewMode === 'table' && (
          <button type="button" className="table-config-btn">
            Personalizar colunas
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="content-section">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Carregando dados dos motoristas...</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="table-view-wrapper">
            <div className="table-scroll">
              <table className="diario-table">
                <thead>
                  <tr>
                    <th rowSpan={2} className="sticky-col left-col motorista-col">Motorista</th>
                    <th rowSpan={2} className="sticky-col left-col gestor-col">Gestor</th>
                    <th colSpan={4}>Resumo do período</th>
                    <th colSpan={weekRanges.length}>Manifestos por semana (Qtd.)</th>
                    <th rowSpan={2} className="acoes-col">Ações</th>
                  </tr>
                  <tr>
                    <th>Dias úteis</th>
                    <th>Trabalhados</th>
                    <th>Não trabalhados</th>
                    <th>% Aproveit.</th>
                    {weekRanges.map((week) => (
                      <th key={week.label}>
                        <div>{week.label}</div>
                        <small>{formatarFaixaSemana(week.start, week.end)}</small>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {motoristasPaginados.map((motorista) => {
                    const manifestosDoMotorista = getManifestosDoMotorista(motorista.id)
                      .sort((a: any, b: any) => {
                        const dataA = getManifestoDate(a);
                        const dataB = getManifestoDate(b);
                        return (dataB?.getTime() || 0) - (dataA?.getTime() || 0);
                      });
                    const semanasMotorista = getManifestosPorSemana(manifestosDoMotorista, weekRanges);
                    const avatarStyle = { backgroundColor: getAvatarColor(motorista.nome) };
                    const isExpanded = expandedMotoristaId === motorista.id;

                    return (
                      <React.Fragment key={`table-${motorista.id}`}>
                        <tr
                          className={`table-master-row ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleExpandedMotorista(motorista.id)}
                        >
                          <td className="sticky-col left-col motorista-col motorista-cell">
                            <div className="table-driver-cell">
                              <button
                                type="button"
                                className={`table-expand-toggle ${isExpanded ? 'expanded' : ''}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleExpandedMotorista(motorista.id);
                                }}
                                title={isExpanded ? 'Ocultar manifestos' : 'Exibir manifestos'}
                              >
                                ▾
                              </button>
                              <div className="table-avatar" style={avatarStyle}>{getInitials(motorista.nome)}</div>
                              <div>
                                <strong>{motorista.nome}</strong>
                              </div>
                            </div>
                          </td>
                          <td className="sticky-col left-col gestor-col">{motorista.responsavel}</td>
                          <td>{motorista.diasATrabalhar}</td>
                          <td className="metric-positive">{motorista.diasTrabalhados}</td>
                          <td className="metric-negative">{motorista.naoTrabalhados}</td>
                          <td className="metric-primary">{motorista.percentual}%</td>
                          {semanasMotorista.map((semana) => (
                            <td key={`${motorista.id}-${semana.label}`}>{semana.total}</td>
                          ))}
                          <td className="acoes-col">
                            <div className="table-actions" onClick={(event) => event.stopPropagation()}>
                              <button
                                type="button"
                                className="table-action-btn"
                                onClick={() => toggleCardFlip(motorista.id)}
                                title="Ver detalhes do card"
                              >
                                ◉
                              </button>
                              <button type="button" className="table-action-btn more" title="Mais ações">⋮</button>
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="table-detail-row">
                            <td colSpan={tableColumnCount} className="table-detail-cell">
                              <div className="manifestos-subtable-wrapper">
                                <div className="manifestos-subtable-header">
                                  <strong>Manifestos do motorista</strong>
                                  <span>{manifestosDoMotorista.length} registro(s)</span>
                                </div>

                                {manifestosDoMotorista.length === 0 ? (
                                  <div className="manifestos-empty-state">
                                    Nenhum manifesto encontrado para este motorista no período selecionado.
                                  </div>
                                ) : (
                                  <table className="manifestos-subtable">
                                    <thead>
                                      <tr>
                                        <th>Manifesto</th>
                                        <th>Status</th>
                                        <th>Placa</th>
                                        <th>Origem</th>
                                        <th>Destino</th>
                                        <th>Partida</th>
                                        <th>Chegada</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {manifestosDoMotorista.map((manifesto: any) => (
                                        <tr key={`${motorista.id}-${manifesto.COD_MANIFESTO || manifesto.NUMERO}`}>
                                          <td>{manifesto.NUMERO || manifesto.COD_MANIFESTO || '-'}</td>
                                          <td>{manifesto.STATUS || '-'}</td>
                                          <td>{manifesto.PLACA_CAVALO || '-'}</td>
                                          <td>{formatarTrecho(manifesto.ORIGEM_CIDADE, manifesto.ORIGEM_UF)}</td>
                                          <td>{formatarTrecho(manifesto.DESTINO_CIDADE, manifesto.DESTINO_UF)}</td>
                                          <td>{formatarDataHora(manifesto.PARTIDA || manifesto.CRIACAO || manifesto.REGISTRO)}</td>
                                          <td>{formatarDataHora(manifesto.CHEGADA || manifesto.ENCERRADO_SEFAZ)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="table-legend">
              <div className="legend-item">
                <span className="legend-dot worked"></span>
                Trabalhado
              </div>
              <div className="legend-item">
                <span className="legend-dot not-worked"></span>
                Não trabalhado
              </div>
              <div className="legend-item">
                <span className="legend-dot outside-month"></span>
                Não útil / Fora do mês
              </div>
            </div>

            <div className="table-pagination">
              <div className="table-pagination-left">
                <span>Linhas por página</span>
                <select
                  className="table-page-size-select"
                  value={tableRowsPerPage}
                  onChange={(e) => {
                    setTableRowsPerPage(Number(e.target.value));
                    setTablePage(1);
                  }}
                >
                  {tableRowsPerPageOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <span>
                  {motoristasOrdenados.length === 0
                    ? '0 de 0'
                    : `${tableStartIndex + 1}-${Math.min(tableEndIndex, motoristasOrdenados.length)} de ${motoristasOrdenados.length}`}
                </span>
              </div>

              <div className="table-pagination-right">
                <button
                  type="button"
                  className="table-page-nav"
                  onClick={() => setTablePage((page) => Math.max(1, page - 1))}
                  disabled={currentTablePage === 1}
                >
                  Anterior
                </button>
                <span className="table-page-indicator">Página {currentTablePage} de {totalTablePages}</span>
                <button
                  type="button"
                  className="table-page-nav"
                  onClick={() => setTablePage((page) => Math.min(totalTablePages, page + 1))}
                  disabled={currentTablePage === totalTablePages}
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="drivers-grid">
            {motoristasOrdenados.map((motorista, index) => {
              const isFlipped = flippedCards.has(motorista.id);
              const manifestosDoMotorista = getManifestosDoMotorista(motorista.id);
              const calendarSlots = getCalendarSlots(periodoFilter, motorista.diasCalendario);
              
              return (
                <div key={motorista.id} className={`driver-card ${isFlipped ? 'flipped' : ''}`} onClick={() => toggleCardFlip(motorista.id)}>
                  <div className="card-inner">
                    {/* Frente do card */}
                    <div className="card-front">
                      <div className="driver-header">
                        <span className="driver-rank">{index + 1}</span>
                        <h3 className="driver-name">
                          {motorista.nome}
                          {motorista.temAlerta && (
                            <span 
                              className="alert-icon" 
                              title={`${motorista.alertas?.length || 0} alerta(s) de manifesto`}
                              style={{
                                marginLeft: '8px',
                                color: '#ff6b35',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              ⚠️
                            </span>
                          )}
                        </h3>
                        <span className="driver-percentage">{motorista.percentual}%</span>
                      </div>
                      
                      <div className="driver-info">
                        <p>Operação: {motorista.responsavel}</p>
                      </div>

                      <div className="driver-metrics">
                        <div className="metric-item">
                          <span className="metric-title">Dias uteis</span>
                          <strong>{motorista.diasATrabalhar}</strong>
                        </div>
                        <div className="metric-item metric-manifestos">
                          <span className="metric-title">Manifestos</span>
                          <strong>{manifestosDoMotorista.length}</strong>
                        </div>
                        <div className="metric-item metric-worked">
                          <span className="metric-title">Trabalhados</span>
                          <strong>{motorista.diasTrabalhados}</strong>
                        </div>
                        <div className="metric-item metric-not-worked">
                          <span className="metric-title">Nao trabalhados</span>
                          <strong>{motorista.naoTrabalhados}</strong>
                        </div>
                      </div>

                      <div className="weekdays-row">
                        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'].map((diaSemana) => (
                          <span key={`${motorista.id}-${diaSemana}`} className="weekday-item">{diaSemana}</span>
                        ))}
                      </div>

                      <div className="calendar-grid" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                        {calendarSlots.map((slot, slotIndex) => {
                          return (
                            <div
                              key={`${motorista.id}-${slotIndex}`}
                              className={`day-circle ${slot.outside ? 'outside-month' : (slot.worked ? 'worked' : 'not-worked')}`}
                            >
                              {slot.dia}
                            </div>
                          );
                        })}
                      </div>

                      <div className="legend">
                        <div className="legend-item">
                          <span className="legend-dot worked"></span>
                          Dias Trabalhados
                        </div>
                        <div className="legend-item">
                          <span className="legend-dot not-worked"></span>
                          Dias não Trabalhados
                        </div>
                        <div className="legend-item">
                          <span className="legend-dot outside-month"></span>
                          Não útil / Fora do mês
                        </div>
                      </div>

                      <div className="card-actions">
                        <button
                          type="button"
                          className="details-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleCardFlip(motorista.id);
                          }}
                        >
                          Ver detalhes
                        </button>
                      </div>
                    </div>

                    {/* Verso do card - Manifestos */}
                    <div className="card-back">
                      <div className="driver-header">
                        <h3 className="driver-name">{motorista.nome}</h3>
                        <span className="manifesto-count">{manifestosDoMotorista.length} manifestos</span>
                      </div>
                      
                      {/* Seção de Alertas */}
                      {motorista.temAlerta && motorista.alertas && motorista.alertas.length > 0 && (
                        <div className="alertas-section" style={{
                          marginBottom: '15px',
                          padding: '10px',
                          backgroundColor: '#fef2f2',
                          borderRadius: '6px',
                          border: '1px solid #fecaca'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                            color: '#dc2626',
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>
                            ⚠️ Alertas de Manifestos ({motorista.alertas.length})
                          </div>
                          {motorista.alertas.map((alerta: any, idx: number) => (
                            <div key={idx} style={{
                              fontSize: '12px',
                              color: '#7f1d1d',
                              marginBottom: '4px',
                              lineHeight: '1.3'
                            }}>
                              • {alerta.descricao}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="manifestos-list">
                        {manifestosDoMotorista.length > 0 ? (
                          manifestosDoMotorista.map((manifesto: any, index: number) => (
                            <div key={index} className="manifesto-item">
                              <div className="manifesto-numero">#{manifesto.NUMERO}</div>
                              <div className="manifesto-datas">
                                <div><strong>Partida:</strong> {manifesto.PARTIDA ? new Date(manifesto.PARTIDA).toLocaleDateString() : 'N/A'}</div>
                                <div><strong>Chegada:</strong> {manifesto.CHEGADA ? new Date(manifesto.CHEGADA).toLocaleDateString() : 'N/A'}</div>
                              </div>
                              <div className={`manifesto-status status-${manifesto.STATUS?.toLowerCase()}`}>
                                {manifesto.STATUS}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-manifestos">
                            <p>Nenhum manifesto no período</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="card-flip-hint">
                        Clique para voltar
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal para motoristas disponíveis para programação */}
      {showMotoristasDisponiveisModal && (
        <div className="modal-overlay" onClick={() => setShowMotoristasDisponiveisModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🚛 Motoristas para Programar</h2>
            <p style={{marginBottom: '15px', color: '#666'}}>
              Motoristas sem manifestos, subutilizados ou parados há tempo
            </p>
            <div className="modal-list">
              {(() => {
                // Combinar e deduplicar motoristas, EXCLUINDO os que estão de licença
                const motoristasUnificados: any[] = [];
                const cpfsProcessados = new Set<string>();
                
                // Criar set de CPFs dos motoristas de licença para exclusão
                const cpfsMotorístasLicenca = new Set(motoristasLicenca.map(m => m.cpf));

                // Primeiro, adicionar motoristas sem manifesto (se não estão de licença)
                motoristasSemManifesto.forEach(m => {
                  // Tentar extrair CPF do motorista baseado no ID dos motoristas principais
                  const motoristaCompleto = motoristas.find(motor => motor.nome === m.nome);
                  const cpf = motoristaCompleto?.id || m.nome; // Usar nome como fallback
                  
                  // EXCLUIR se está de licença
                  if (!cpfsProcessados.has(cpf) && !cpfsMotorístasLicenca.has(cpf)) {
                    cpfsProcessados.add(cpf);
                    motoristasUnificados.push({
                      nome: m.nome,
                      gestor: m.responsavel,
                      cpf: cpf,
                      categoria: 'Sem manifesto no período',
                      prioridade: 1,
                      manifestosNoPeriodo: 0,
                      ultimoManifesto: m.ultimoManifesto,
                      tipo: 'sem-manifesto'
                    });
                  }
                });

                // Depois, adicionar motoristas disponíveis (se não duplicados e não estão de licença)
                motoristasDisponiveis.forEach(m => {
                  // EXCLUIR se está de licença
                  if (!cpfsProcessados.has(m.cpf) && !cpfsMotorístasLicenca.has(m.cpf)) {
                    cpfsProcessados.add(m.cpf);
                    motoristasUnificados.push({
                      nome: m.motorista,
                      gestor: m.gestor,
                      cpf: m.cpf,
                      categoria: m.categoria,
                      prioridade: m.prioridade,
                      manifestosNoPeriodo: m.manifestosNoPeriodo,
                      ultimoManifestoData: m.ultimoManifestoData,
                      ultimoManifestoNumero: m.ultimoManifestoNumero,
                      ultimoManifestoStatus: m.ultimoManifestoStatus,
                      diasSemManifesto: m.diasSemManifesto,
                      tipo: 'disponivel'
                    });
                  }
                });

                // Ordenar por prioridade
                motoristasUnificados.sort((a, b) => a.prioridade - b.prioridade);

                return motoristasUnificados.map((m, idx) => (
                  <div key={`unificado-${idx}`} className="modal-list-item">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                      <div style={{flex: 1}}>
                        <strong>{m.nome}</strong>
                        <div style={{fontSize: '12px', color: '#666', marginBottom: '8px'}}>
                          CPF: {m.cpf} | Gestor: {m.gestor}
                        </div>
                      </div>
                      <div style={{textAlign: 'right'}}>
                        <span style={{
                          background: m.prioridade <= 2 ? '#dc2626' : m.prioridade === 3 ? '#d97706' : '#059669',
                          color: 'white',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          {m.categoria}
                        </span>
                      </div>
                    </div>
                    <div style={{marginTop: '8px', fontSize: '13px'}}>
                      <div>📊 Manifestos no período: <strong>{m.manifestosNoPeriodo}</strong></div>
                      {m.tipo === 'sem-manifesto' && m.ultimoManifesto && (
                        <div>📅 Último manifesto: <strong>{m.ultimoManifesto}</strong></div>
                      )}
                      {m.tipo === 'disponivel' && m.ultimoManifestoData && (
                        <>
                          <div>📅 Último manifesto: <strong>#{m.ultimoManifestoNumero}</strong> em {new Date(m.ultimoManifestoData).toLocaleDateString()}</div>
                          <div>📋 Status: <strong>{m.ultimoManifestoStatus}</strong></div>
                        </>
                      )}
                      {m.diasSemManifesto !== null && m.diasSemManifesto !== undefined && (
                        <div style={{color: m.diasSemManifesto >= 7 ? '#dc2626' : m.diasSemManifesto >= 3 ? '#d97706' : '#059669'}}>
                          ⏰ <strong>{m.diasSemManifesto}</strong> dias sem manifesto
                        </div>
                      )}
                    </div>
                  </div>
                ));
              })()}

              {motoristasSemManifesto.length === 0 && motoristasDisponiveis.length === 0 && (
                <div style={{textAlign: 'center', color: '#666', padding: '20px'}}>
                  🎉 Todos os motoristas estão bem utilizados!
                </div>
              )}
            </div>
            <button className="modal-close-btn" onClick={() => setShowMotoristasDisponiveisModal(false)}>Fechar</button>
          </div>
        </div>
      )}

      {showCadastroMotoristasModal && (
        <div className="modal-overlay" onClick={() => setShowCadastroMotoristasModal(false)}>
          <div className="modal-content cadastro-motoristas-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🚛 Cadastro de motoristas do Diário</h2>
            <p style={{ marginBottom: '12px', color: '#666' }}>
              Selecione e aprove os motoristas que devem aparecer no Diário de Bordo
            </p>

            <div className="cadastro-toolbar">
              <input
                type="text"
                className="cadastro-search-input"
                placeholder="Buscar por nome ou CPF..."
                value={filtroCadastroMotoristas}
                onChange={(e) => setFiltroCadastroMotoristas(e.target.value)}
              />
              <div className="cadastro-counter">
                Selecionados: {motoristasCadastroSelecionados.size}
              </div>
            </div>

            <div className="modal-list cadastro-lista-motoristas">
              {loadingCadastroMotoristas ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '24px' }}>Carregando motoristas...</div>
              ) : (
                motoristasCadastroOpcoes
                  .filter((m) => {
                    const termo = filtroCadastroMotoristas.trim().toLowerCase();
                    if (!termo) return true;
                    return (
                      String(m.nome || '').toLowerCase().includes(termo) ||
                      String(m.cpf || '').toLowerCase().includes(termo)
                    );
                  })
                  .map((motorista) => {
                    const cpf = String(motorista.cpf || '').trim();
                    const selecionado = motoristasCadastroSelecionados.has(cpf);
                    return (
                      <label key={cpf} className={`cadastro-item ${selecionado ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selecionado}
                          onChange={() => alternarSelecaoMotoristaCadastro(cpf)}
                        />
                        <div className="cadastro-item-info">
                          <strong>{motorista.nome}</strong>
                          <span>CPF: {cpf}</span>
                        </div>
                      </label>
                    );
                  })
              )}

              {!loadingCadastroMotoristas && motoristasCadastroOpcoes.length === 0 && (
                <div style={{ textAlign: 'center', color: '#666', padding: '24px' }}>
                  Nenhum motorista encontrado na consulta VOLKSWAGEN.
                </div>
              )}
            </div>

            <div className="cadastro-actions">
              <button className="modal-close-btn" onClick={() => setShowCadastroMotoristasModal(false)}>
                Cancelar
              </button>
              <button className="modal-save-btn" onClick={salvarMotoristasCadastro} disabled={salvandoCadastroMotoristas}>
                {salvandoCadastroMotoristas ? 'Salvando...' : 'Aprovar selecionados'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para motoristas de licença */}
      {showMotoristasLicencaModal && (
        <div className="modal-overlay" onClick={() => setShowMotoristasLicencaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🏥 Motoristas de Licença</h2>
            <p style={{marginBottom: '15px', color: '#666'}}>
              Motoristas com ausências aprovadas no período
            </p>
            <div className="modal-list">
              {(() => {
                // Filtrar motoristas de licença por gestor se necessário
                let motoristasLicencaFiltrados = motoristasLicenca;
                if (gestorFilter !== 'todos') {
                  motoristasLicencaFiltrados = motoristasLicenca.filter(m => 
                    m.gestor && m.gestor.toLowerCase() === gestorFilter.toLowerCase()
                  );
                }

                return motoristasLicencaFiltrados.map((m, idx) => (
                  <div key={`licenca-${idx}`} className="modal-list-item">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                      <div style={{flex: 1}}>
                        <strong>{m.motorista}</strong>
                        <div style={{fontSize: '12px', color: '#666', marginBottom: '8px'}}>
                          CPF: {m.cpf} | Gestor: {m.gestor}
                        </div>
                      </div>
                      <div style={{textAlign: 'right'}}>
                        <span style={{
                          background: '#dc2626',
                          color: 'white',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          De Licença
                        </span>
                      </div>
                    </div>
                    <div style={{marginTop: '8px', fontSize: '13px'}}>
                      <div>📊 Manifestos no período: <strong>{m.manifestosNoPeriodo}</strong></div>
                      <div style={{marginTop: '8px', fontWeight: 'bold', color: '#dc2626'}}>
                        🏥 Ausências no período:
                      </div>
                      {m.ausencias && m.ausencias.map((ausencia: any, ausIdx: number) => (
                        <div key={ausIdx} style={{
                          marginTop: '4px', 
                          padding: '8px', 
                          backgroundColor: '#fef2f2', 
                          borderRadius: '4px',
                          border: '1px solid #fecaca'
                        }}>
                          <div style={{fontWeight: 'bold', color: '#dc2626'}}>
                            {ausencia.DESCRICAO || 'Ausência'}
                          </div>
                          <div style={{fontSize: '12px', color: '#666'}}>
                            📅 De: {new Date(ausencia.INICIO).toLocaleDateString()} até {new Date(ausencia.TERMINO).toLocaleDateString()}
                          </div>
                          <div style={{fontSize: '12px', color: '#666'}}>
                            📊 Duração: {Math.ceil((new Date(ausencia.TERMINO).getTime() - new Date(ausencia.INICIO).getTime()) / (1000 * 60 * 60 * 24))} dias
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}

              {motoristasLicenca.length === 0 && (
                <div style={{textAlign: 'center', color: '#666', padding: '20px'}}>
                  ✅ Nenhum motorista com ausência no período!
                </div>
              )}
            </div>
            <button className="modal-close-btn" onClick={() => setShowMotoristasLicencaModal(false)}>Fechar</button>
          </div>
        </div>
      )}

      {/* Modal para manifestos com problemas */}
      {showManifestosAlertaModal && (
        <div className="modal-overlay" onClick={() => setShowManifestosAlertaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>⚠️ Manifestos com Problemas</h2>
            <p style={{marginBottom: '15px', color: '#666'}}>
              Manifestos com inconsistências ou em aberto há muito tempo
            </p>
            <div className="modal-list">
              {(() => {
                // Coletar todos os alertas dos motoristas filtrados
                const todosAlertas: any[] = [];
                
                motoristasFiltrados.forEach(motorista => {
                  if (motorista.temAlerta && motorista.alertas) {
                    motorista.alertas.forEach(alerta => {
                      todosAlertas.push({
                        ...alerta,
                        nomeMotorista: motorista.nome,
                        gestorMotorista: motorista.responsavel
                      });
                    });
                  }
                });

                // Não precisa mais filtrar por gestor aqui, pois já usamos motoristasFiltrados
                return todosAlertas.map((alerta, idx) => (
                  <div key={`alerta-${idx}`} className="modal-list-item">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                      <div style={{flex: 1}}>
                        <strong>Manifesto #{alerta.manifesto}</strong>
                        <div style={{fontSize: '14px', color: '#333', marginBottom: '4px'}}>
                          <strong>Motorista:</strong> {alerta.nomeMotorista}
                        </div>
                        <div style={{fontSize: '12px', color: '#666', marginBottom: '8px'}}>
                          <strong>Gestor:</strong> {alerta.gestorMotorista}
                        </div>
                      </div>
                      <div style={{textAlign: 'right'}}>
                        <span style={{
                          background: '#f59e0b',
                          color: 'white',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          {alerta.tipo === 'MANIFESTO_EM_TRANSITO_COM_POSTERIORES' ? 'Em Trânsito' : 'Sem Chegada'}
                        </span>
                      </div>
                    </div>
                    <div style={{marginTop: '8px', fontSize: '13px'}}>
                      <div style={{
                        color: '#dc2626',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                        📋 Problema:
                      </div>
                      <div style={{color: '#374151', lineHeight: '1.4'}}>
                        {alerta.descricao}
                      </div>
                      {alerta.dataPartida && (
                        <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                          📅 Data de partida: {new Date(alerta.dataPartida).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                      {alerta.dataCriacao && (
                        <div style={{fontSize: '12px', color: '#666'}}>
                          📝 Criado em: {new Date(alerta.dataCriacao).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                    </div>
                  </div>
                ));
              })()}

              {(() => {
                // Contar alertas filtrados por gestor
                let totalAlertasFiltrados = 0;
                motoristasFiltrados.forEach(motorista => {
                  if (motorista.temAlerta && motorista.alertas) {
                    totalAlertasFiltrados += motorista.alertas.length;
                  }
                });
                
                return totalAlertasFiltrados === 0 && (
                  <div style={{textAlign: 'center', color: '#666', padding: '20px'}}>
                    ✅ Nenhum manifesto com problemas no período!
                  </div>
                );
              })()}
            </div>
            <button className="modal-close-btn" onClick={() => setShowManifestosAlertaModal(false)}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiarioModule;